import { useCallback } from 'react'

import { useListBrandTokensQuery } from './useBrandTokens'

const BRAND_COLOR_TOKEN_PREFIX = 'brand:color:'
const BRAND_GRAPHIC_TOKEN_PREFIX = 'brand:graphic:'
const BRAND_TYPOGRAPHY_TOKEN_PREFIX = 'brand:typography:'

const useBrandColor = () => {
  const { data: brandColors } = useListBrandTokensQuery('color')

  return useCallback(
    (colorKey: string | undefined) => {
      if (!colorKey) return undefined
      if (!colorKey.startsWith(BRAND_COLOR_TOKEN_PREFIX)) return colorKey

      const brandColor = brandColors?.find(
        (c) => c.key === colorKey.substring(BRAND_COLOR_TOKEN_PREFIX.length)
      )

      if (brandColor) return brandColor.value
      return undefined
    },
    [brandColors]
  )
}

const useBrandGraphic = () => {
  const { data: graphics } = useListBrandTokensQuery('graphic')

  return useCallback(
    (graphicUrl: string | undefined) => {
      if (!graphicUrl) return undefined
      if (!graphicUrl.startsWith(BRAND_GRAPHIC_TOKEN_PREFIX)) return graphicUrl

      const graphic = graphics?.find(
        (g) => g.key === graphicUrl.substring(BRAND_GRAPHIC_TOKEN_PREFIX.length)
      )

      if (graphic) return graphic.value
      return undefined
    },
    [graphics]
  )
}

const useBrandTypography = () => {
  const { data: brandTypographyList } = useListBrandTokensQuery('typography')

  return useCallback(
    (typographyKey: string | undefined) => {
      if (!typographyKey) return undefined
      if (!typographyKey.startsWith(BRAND_TYPOGRAPHY_TOKEN_PREFIX))
        return typographyKey

      const brandTypography = brandTypographyList?.find(
        (c) =>
          c.key ===
          typographyKey.substring(BRAND_TYPOGRAPHY_TOKEN_PREFIX.length)
      )

      if (brandTypography) return brandTypography.string
      return undefined
    },
    [brandTypographyList]
  )
}

type BrandTypography = {
  family: string
  fallback?: string
  weight?: number
  size: number
  lineHeight?: number
  style?: string
}

const DEFAULT_TYPOGRAPHY = {
  family: 'Inter',
  fallback: 'sans-serif',
  weight: 400,
  size: 12,
  lineHeight: 1.5,
  style: 'normal',
} satisfies BrandTypography

const parseTypography = (
  fontShorthand: string | undefined
): BrandTypography => {
  if (!fontShorthand) {
    return DEFAULT_TYPOGRAPHY
  }
  const regex =
    /(?:(?<style>italic|normal|oblique)\s)?(?:(?<weight>\d{3})\s)?((?<size>\d+)px)?(?:\/(?<lineHeight>\d+(?:\.\d+)?))?\s+"(?<family>.+)"(?:,(?<fallback>[\w\s,-]+))?/
  const match = fontShorthand.match(regex)

  if (!match?.groups) {
    return DEFAULT_TYPOGRAPHY
  }

  const { style, weight, size, lineHeight, family, fallback } = match.groups
  return {
    family,
    fallback,
    style,
    size: parseInt(size, 10),
    weight: weight ? parseInt(weight, 10) : undefined,
    lineHeight: lineHeight ? parseInt(lineHeight, 10) : undefined,
  }
}

export {
  useBrandColor,
  useBrandGraphic,
  useBrandTypography,
  parseTypography,
  type BrandTypography,
}
